import React from "react";
import api from "@/lib/api";
import { useLiveQuery, TAGS, invalidate } from "@/lib/liveSync";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";

export const BADGE_COLORS = {
  violet: "bg-violet-600 text-white",
  orange: "bg-orange-500 text-white",
  sky: "bg-sky-600 text-white",
  rose: "bg-rose-600 text-white",
  teal: "bg-teal-600 text-white",
  lime: "bg-lime-600 text-white",
};

export function useBusinessAgreements() {
  const { data, refetch } = useLiveQuery(
    [TAGS.CORPORATE, TAGS.SESSIONS],
    async () => (await api.get("/business-agreements")).data,
    { intervalMs: 10000 },
  );
  return { agreements: data || [], refetch };
}

export function BusinessBadge({ session, className = "" }) {
  if (!session?.business_account_id) return null;
  const over = session.business_over_quota;
  return (
    <Badge
      data-testid={`business-badge-${session.plate}`}
      className={`rounded-sm text-[9px] uppercase tracking-widest border-transparent gap-1 ${over ? "bg-red-600 text-white" : "bg-violet-600 text-white"} ${className}`}
    >
      <Storefront size={10} weight="fill" />
      {session.business_name}{over ? " · KONTENJAN DOLU" : ""}
    </Badge>
  );
}

const NONE = "__none__";

export const OPERATOR_ACTIVE_TAG_WINDOW_MIN = 30;

export function canTagSession(user, session) {
  if (!user || !session) return false;
  if (session.payment_status === "odendi" || session.business_payment_status === "tahsil_edildi") return false;
  if (user.role === "operator") {
    if (session.business_account_id) return false;
    if (session.status === "closed" && session.exit_time) return true;
    // Aktif araç: girişten sonraki ilk 30 dk içinde bir kez
    const elapsedMin = (Date.now() - new Date(session.entry_time).getTime()) / 60000;
    return elapsedMin <= OPERATOR_ACTIVE_TAG_WINDOW_MIN;
  }
  return ["admin", "super_admin"].includes(user.role);
}

export function BusinessQuickTag({ session, onUpdated, className = "" }) {
  const { user } = useAuth();
  const { agreements } = useBusinessAgreements();
  const [busy, setBusy] = React.useState(false);
  if (!session) return null;
  const allowed = canTagSession(user, session);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  if (!session.business_account_id && (!allowed || agreements.length === 0)) return null;

  const apply = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${session.id}/business-tag`, { corporate_account_id: id });
      if (!id) toast.success(`${session.plate} işletme etiketi kaldırıldı`);
      else if (data.business_over_quota) toast.warning(`${data.business_name} kontenjanı dolu — normal tarife uygulanır`);
      else toast.success(`${session.plate} → ${data.business_name}`);
      invalidate([TAGS.SESSIONS, TAGS.UNPAID, TAGS.CORPORATE]);
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Etiketlenemedi"); }
    finally { setBusy(false); }
  };

  if (session.business_account_id) {
    return (
      <div className={`flex items-center gap-2 flex-wrap ${className}`} data-testid={`business-quick-${session.plate}`}>
        <BusinessBadge session={session} className="text-[10px] py-1" />
        {isAdmin && !busy && session.business_payment_status !== "tahsil_edildi" && session.payment_status !== "odendi" && (
          <button type="button" onClick={() => apply(null)} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive underline"
                  data-testid={`business-quick-remove-${session.plate}`}>Kaldır</button>
        )}
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${className}`} data-testid={`business-quick-${session.plate}`}>
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Storefront size={12} /> İşletme:</span>
      {agreements.map((a) => {
        const color = BADGE_COLORS[a.badge_color] || BADGE_COLORS.violet;
        return (
          <button key={a.id} type="button" disabled={busy} onClick={(e) => { e.stopPropagation(); apply(a.id); }}
                  data-testid={`business-quick-${session.plate}-${a.id}`}
                  className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${color} opacity-90 hover:opacity-100 active:scale-95 transition disabled:opacity-40`}>
            {a.name}{a.agreement_type === "quota" ? ` ${a.active_count}/${a.quota_slots}` : ""}
          </button>
        );
      })}
    </div>
  );
}

export function BusinessTagSelect({ session, onUpdated, className = "", size = "sm", compact = false }) {
  const { user } = useAuth();
  const { agreements } = useBusinessAgreements();
  const [busy, setBusy] = React.useState(false);
  if (!session) return null;
  const value = session.business_account_id || NONE;
  const allowed = canTagSession(user, session);
  const isOperator = user?.role === "operator";

  const change = async (v) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${session.id}/business-tag`, {
        corporate_account_id: v === NONE ? null : v,
      });
      if (v === NONE) toast.success(`${session.plate} işletme etiketi kaldırıldı`);
      else if (data.business_over_quota) toast.warning(`${data.business_name} kontenjanı dolu — normal tarife uygulanır`);
      else toast.success(`${session.plate} → ${data.business_name}`);
      invalidate([TAGS.SESSIONS, TAGS.UNPAID, TAGS.CORPORATE]);
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Etiketlenemedi");
    } finally { setBusy(false); }
  };

  if (agreements.length === 0 && !session.business_account_id) return null;
  if (!allowed) return null;
  const h = size === "sm" ? "h-8 text-[11px]" : "h-10 text-sm";
  if (compact) {
    return (
      <Select value={value} onValueChange={change} disabled={busy}>
        <SelectTrigger
          className={`rounded-sm h-7 w-7 p-0 justify-center border-dashed text-muted-foreground hover:text-violet-600 hover:border-violet-400 [&>svg:last-child]:hidden ${className}`}
          data-testid={`business-tag-select-${session.plate}`}
          onClick={(e) => e.stopPropagation()}
          title={session.business_account_id ? "İşletme etiketini değiştir" : "İşletme müşterisi olarak etiketle"}
        >
          <Storefront size={14} />
        </SelectTrigger>
        <SelectContent>
          {!isOperator && <SelectItem value={NONE}>— İşletme yok —</SelectItem>}
          {agreements.map((a) => (
            <SelectItem key={a.id} value={a.id} data-testid={`business-tag-option-${a.id}`}>
              {a.name}
              {a.agreement_type === "quota"
                ? ` (${a.active_count}/${a.quota_slots}${a.quota_full ? " DOLU" : ""})`
                : ` (ilk ${a.covered_hours} sa)`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <Select value={value} onValueChange={change} disabled={busy}>
      <SelectTrigger
        className={`rounded-sm ${h} ${session.business_account_id ? "border-violet-400 bg-violet-50" : ""} ${className}`}
        data-testid={`business-tag-select-${session.plate}`}
        onClick={(e) => e.stopPropagation()}
      >
        <Storefront size={12} className="mr-1 text-violet-600 shrink-0" />
        <SelectValue placeholder="İşletme" />
      </SelectTrigger>
      <SelectContent>
        {!isOperator && <SelectItem value={NONE}>— İşletme yok —</SelectItem>}
        {agreements.map((a) => (
          <SelectItem key={a.id} value={a.id} data-testid={`business-tag-option-${a.id}`}>
            {a.name}
            {a.agreement_type === "quota"
              ? ` (${a.active_count}/${a.quota_slots}${a.quota_full ? " DOLU" : ""})`
              : ` (ilk ${a.covered_hours} sa)`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function BusinessQuotaStrip() {
  const { agreements } = useBusinessAgreements();
  if (!agreements.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="business-quota-strip">
      {agreements.map((a) => {
        const color = BADGE_COLORS[a.badge_color] || BADGE_COLORS.violet;
        return (
          <div
            key={a.id}
            data-testid={`business-quota-${a.id}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs ${a.quota_full ? "border-red-500 bg-red-50" : "border-border bg-card"}`}
          >
            <span className={`w-2 h-2 rounded-full ${color.split(" ")[0]}`} />
            <span className="font-semibold uppercase tracking-widest">{a.name}</span>
            {a.agreement_type === "quota" ? (
              <span className={`font-mono font-bold ${a.quota_full ? "text-red-600" : "text-foreground"}`}>
                {a.active_count}/{a.quota_slots}{a.quota_full ? " · DOLU" : ""}
              </span>
            ) : (
              <span className="font-mono text-muted-foreground">{a.active_count} araç içeride · ilk {a.covered_hours} sa</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
